// Remote-player avatars (NET-2, M26.2). One hue-tinted humanoid (body + head) per
// connected peer, pooled by entity id, its position eased toward the latest server
// pose each frame so 10 Hz Move batches read as smooth motion. Client-only (ENG-1).
// Nametags layer on in M27.
import * as THREE from 'three';
import type { RemotePlayer } from '../net/client';

interface Avatar {
  mesh: THREE.Group;
  mats: THREE.Material[];
  tex: THREE.Texture[];
  render: { x: number; y: number; z: number; yaw: number };
  /** The nametag sprite + its current status, so the tag texture is only
   *  regenerated when status actually changes (§30.4) — not every frame. */
  tag: { sprite: THREE.Sprite; mat: THREE.SpriteMaterial; tex: THREE.CanvasTexture };
  status: 'available' | 'focus' | 'call';
}

/** Per-frame easing toward the server pose (0..1); ~0.35 hides 10 Hz stepping. */
const EASE = 0.35;

export class RemotePlayerRenderer {
  private readonly group = new THREE.Group();
  private readonly avatars = new Map<number, Avatar>();

  constructor(scene: THREE.Scene) {
    scene.add(this.group);
  }

  update(players: readonly RemotePlayer[]): void {
    const seen = new Set<number>();
    for (const p of players) {
      seen.add(p.id);
      let a = this.avatars.get(p.id);
      if (!a) { a = this.make(p); this.avatars.set(p.id, a); this.group.add(a.mesh); }
      // Ease position; snap yaw through the shortest arc.
      a.render.x += (p.x - a.render.x) * EASE;
      a.render.y += (p.y - a.render.y) * EASE;
      a.render.z += (p.z - a.render.z) * EASE;
      a.render.yaw += shortestAngle(a.render.yaw, p.yaw) * EASE;
      a.mesh.position.set(a.render.x, a.render.y, a.render.z);
      a.mesh.rotation.y = a.render.yaw;
      const status = p.status ?? 'available';
      if (status !== a.status) {
        a.tag.tex.dispose();
        a.tag.mat.dispose();
        const fresh = makeNametag(p.name, status);
        a.mesh.remove(a.tag.sprite);
        fresh.sprite.position.y = 2.3;
        a.mesh.add(fresh.sprite);
        a.mats = a.mats.filter((m) => m !== a.tag.mat).concat(fresh.mat);
        a.tex = a.tex.filter((t) => t !== a.tag.tex).concat(fresh.tex);
        a.tag = fresh;
        a.status = status;
      }
    }
    for (const [id, a] of this.avatars) {
      if (seen.has(id)) continue;
      this.dispose(a);
      this.avatars.delete(id);
    }
  }

  private make(p: RemotePlayer): Avatar {
    const color = new THREE.Color().setHSL((p.hue % 360) / 360, 0.6, 0.55);
    const skin = new THREE.MeshBasicMaterial({ color });
    const head = new THREE.MeshBasicMaterial({ color: color.clone().offsetHSL(0, 0, 0.12) });
    const mesh = new THREE.Group();
    const bodyGeo = new THREE.BoxGeometry(0.6, 1.2, 0.3);
    const body = new THREE.Mesh(bodyGeo, skin);
    body.position.y = 0.9; // feet at group origin
    const headGeo = new THREE.BoxGeometry(0.5, 0.5, 0.5);
    const headMesh = new THREE.Mesh(headGeo, head);
    headMesh.position.y = 1.75;
    mesh.add(body, headMesh);
    // Nametag (M27): a camera-facing label floating above the head, ringed by
    // presence status (§30.4, COW-5).
    const status = p.status ?? 'available';
    const tag = makeNametag(p.name, status);
    tag.sprite.position.y = 2.3;
    mesh.add(tag.sprite);
    mesh.position.set(p.x, p.y, p.z);
    return { mesh, mats: [skin, head, tag.mat], tex: [tag.tex], render: { x: p.x, y: p.y, z: p.z, yaw: p.yaw }, tag, status };
  }

  private dispose(a: Avatar): void {
    this.group.remove(a.mesh);
    a.mesh.traverse((o) => { if (o instanceof THREE.Mesh) o.geometry.dispose(); });
    for (const m of a.mats) m.dispose();
    for (const t of a.tex) t.dispose();
  }

  clear(): void {
    for (const a of this.avatars.values()) this.dispose(a);
    this.avatars.clear();
  }
}

/** Ring colours for presence status (§30.4, COW-5): grey / amber / blue. */
const STATUS_RING_COLOR: Record<'available' | 'focus' | 'call', string> = {
  available: '#9aa5b5',
  focus: '#e0a548',
  call: '#4d8fe0',
};

/** A camera-facing name label drawn to a canvas texture (M27 nametags), ringed
 *  by the player's presence status (§30.4, COW-5) — a coloured border, not a
 *  separate mesh, so it always faces the camera with the name. */
export function makeNametag(
  name: string,
  status: 'available' | 'focus' | 'call' = 'available',
): { sprite: THREE.Sprite; mat: THREE.SpriteMaterial; tex: THREE.CanvasTexture } {
  const canvas = document.createElement('canvas');
  const measure = canvas.getContext('2d')!;
  measure.font = 'bold 48px ui-monospace, monospace';
  const w = Math.max(64, Math.ceil(measure.measureText(name).width) + 28);
  canvas.width = w;
  canvas.height = 64;
  const ctx = canvas.getContext('2d')!;
  ctx.font = 'bold 48px ui-monospace, monospace';
  ctx.fillStyle = 'rgba(0,0,0,0.55)';
  ctx.fillRect(0, 0, w, 64);
  const ring = STATUS_RING_COLOR[status];
  ctx.strokeStyle = ring;
  ctx.lineWidth = 4;
  ctx.strokeRect(2, 2, w - 4, 60);
  ctx.fillStyle = '#f0f4fb';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(name, w / 2, 34);
  const tex = new THREE.CanvasTexture(canvas);
  const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false, depthWrite: false });
  const sprite = new THREE.Sprite(mat);
  const s = 0.006;
  sprite.scale.set(w * s, 64 * s, 1);
  return { sprite, mat, tex };
}

/** Signed shortest angular delta from `from` to `to` (radians). */
function shortestAngle(from: number, to: number): number {
  let d = (to - from) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return d;
}
